import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import Swal from "sweetalert2";

import { AuthContext } from "../contexts/auth";
import { uploadDocumentiSingolo, getDocumentRecipientsDefault } from "../services/api";
import RecipientsField from "./recipients_field";
import { splitEmails, isEmail, normalizeEmailCsv, sameEmailSet, buildDefaultEmailCsv } from "../services/utils_email";

/* ---------------- helpers ---------------- */

const clampTiming = (n) => {
  if (n == null) return null;
  let x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return null;
  if (x < 1) x = 1;
  if (x > 31) x = 31;
  return x;
};


// “effective” = quello che verrebbe salvato se custom (input oppure default)
// “matchesDefault” = coincide con i default? allora mando null/null
const buildEffectiveNotifications = ({ timingInput, emailInput, defaultDays, defaultEmailCsv }) => {
  const defaultTiming = clampTiming(defaultDays);
  const defaultEmail = normalizeEmailCsv(defaultEmailCsv);

  const effectiveTiming =
    String(timingInput || "").trim() === "" ? defaultTiming : clampTiming(timingInput);

  const effectiveEmail =
    String(emailInput || "").trim() === "" ? defaultEmail : normalizeEmailCsv(emailInput);

  const matchesDefault =
    effectiveTiming === defaultTiming && sameEmailSet(effectiveEmail, defaultEmail);

  return { defaultTiming, defaultEmail, effectiveTiming, effectiveEmail, matchesDefault };
};

const getInitialValues = (employeeId = null) => ({
  name: "",
  description: "",
  due_date: "",
  notification_timing: "",
  notification_email: "",
  notify_employee: true,
  visible_to_employee: true,
  typology: "cu",
  employee_id: employeeId,
});

const isValidDueDateRange = (value) => {
  if (!value) return true;

  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const [year, month, day] = value.split("-").map(Number);

  if (year < 2000 || year > 2200) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export default function DocumentForm({ employee, onSuccess, disabled = false }) {
  const { token, profile } = useContext(AuthContext);

  const employeeId = employee?.id ?? null;

  const [values, setValues] = useState(() => getInitialValues(employeeId));
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(null);
  const employeeIsActive = !!employee?.app_activate && !!employee?.active_contract;
  
  // defaults endpoint
  const [defaults, setDefaults] = useState({
    loading: false,
    loaded: false,
    default_days: null,
    send_company: false,
    send_handlers: false,
    company_email: "",
    company_label: "Azienda",
    handlers_emails: [],
    others_emails: "",
  });

  // cache per employee
  const cacheRef = useRef(new Map()); // employeeId -> payload

  const levelValues = Object.values(profile?.level || {}).flat();
  const hasShiftsLevel = levelValues.includes("shifts");
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  // reset quando cambia employee
  useEffect(() => {
    setValues(getInitialValues(employeeId));
    setFile(null);
    setErrors(null);
  }, [employeeId]);

  // fetch defaults (una volta per employee)
  useEffect(() => {
    if (!token || !employeeId) return;

    const fromCache = cacheRef.current.get(employeeId);
    if (fromCache) {
      setDefaults({ loading: false, loaded: true, ...fromCache });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setDefaults((p) => ({ ...p, loading: true }));

        const data = await getDocumentRecipientsDefault(token, employeeId);
        if (cancelled) return;

        const payload = {
          default_days: data?.default_days ?? null,
          send_company: !!data?.send_company,
          send_handlers: !!data?.send_handlers,
          company_email: String(data?.company_email || "").trim().toLowerCase(),
          company_label: String(data?.company_label || "Azienda").trim(),
          handlers_emails: Array.isArray(data?.handlers_emails) ? data.handlers_emails : [],
          others_emails: String(data?.others_emails || ""), 
        };
        
        cacheRef.current.set(employeeId, payload);
        setDefaults({ loading: false, loaded: true, ...payload });
      } catch {
        if (cancelled) return;
        setDefaults({
          loading: false,
          loaded: true,
          default_days: null,
          send_company: false,
          send_handlers: false,
          company_email: "",
          company_label: "Azienda",
          handlers_emails: [],
          others_emails: "",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, employeeId]);

  const setField = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextVal = type === "checkbox" ? !!checked : value;

    setField(name, nextVal);

    setErrors((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[name];
      return Object.keys(next).length ? next : null;
    });
  };

  const handleSelectedFile = (f) => {
    if (!f) return;

    // Controllo dimensione massima 10 MB
    if (f.size > MAX_FILE_SIZE) {
      setFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setErrors((prev) => ({
        ...prev,
        file: ["Il file selezionato supera la dimensione massima consentita di 10 MB"],
      }));

      return;
    }

    setFile(f);
    setErrors((prev) => {
      if (!prev?.file) return prev;
      const next = { ...prev };
      delete next.file;
      return Object.keys(next).length ? next : null;
    });
  };

  // derived defaults (per UI + confronto)
  const defaultEmailCsv = useMemo(() => {
    if (!defaults.loaded) return "";
    if (defaults.default_days == null) return ""; // <-- chiave
    return buildDefaultEmailCsv({
      send_company: defaults.send_company,
      company_email: defaults.company_email,
      send_handlers: defaults.send_handlers,
      handlers_emails: defaults.handlers_emails,
      others_emails: defaults.others_emails,
    });
  }, [
    defaults.loaded,
    defaults.default_days,
    defaults.send_company,
    defaults.company_email,
    defaults.send_handlers,
    defaults.handlers_emails,
    defaults.others_emails,
  ]);
  const defaultTimingStr = useMemo(
    () => (defaults.loaded && defaults.default_days != null ? String(defaults.default_days) : ""),
    [defaults.loaded, defaults.default_days]
  );

  const defaultsEnabled = defaults.loaded && defaults.default_days != null;

  // ciò che l’utente “vede” (se input vuoto -> default)
  const shownTiming = useMemo(() => {
    const raw = String(values.notification_timing ?? "").trim();
    if (raw !== "") return raw;
    return defaultsEnabled ? String(defaultTimingStr ?? "").trim() : "";
  }, [values.notification_timing, defaultsEnabled, defaultTimingStr]);

  const shownEmail = useMemo(() => {
    const raw = String(values.notification_email ?? "").trim();
    if (raw !== "") return raw;
    return defaultsEnabled ? String(defaultEmailCsv ?? "").trim() : "";
  }, [values.notification_email, defaultsEnabled, defaultEmailCsv]);

  const hasDueDate = !!values.due_date;

  const reminderEnabled = useMemo(() => {
    if (!hasDueDate) return false;

    const n = clampTiming(shownTiming);
    return n != null && n >= 1;
  }, [hasDueDate, shownTiming]);
  
  // Mostro destinatari solo se c’è due_date
  const recipientsVisible = hasDueDate;
  const recipientsDisabled = !reminderEnabled;

  // company/handlers/others per RecipientsField
  const companyEmail = String(defaults.company_email || profile?.company?.email || "").trim();
  const companyName = String(defaults.company_label || profile?.company?.name || "Azienda").trim();
  const handlers = Array.isArray(defaults.handlers_emails) ? defaults.handlers_emails : [];
  const others = String(defaults.others_emails || "");
  
  const loadSuggested = useCallback(() => Promise.resolve(defaults.loaded ? defaults : null), [defaults]);

  const validate = useCallback(() => {
    const errs = {};

    if (!values.visible_to_employee && values.notify_employee) {
      errs.notify_employee = ["Non puoi notificare il dipendente se il documento non è visibile."];
    }

    if (!file) errs.file = ["Il file è obbligatorio."];
    if (!values.name?.trim()) errs.name = ["Il nome documento è obbligatorio."];
    if (!values.employee_id) errs.employee_id = ["Identificativo dipendente mancante."];

    if (values.due_date && !isValidDueDateRange(values.due_date)) {
      errs.due_date = [
        "La data di scadenza deve essere compresa tra il 01/01/2000 e il 31/12/2200."
      ];
    }

    // Valida timing SOLO se l’utente ha scritto qualcosa
    if (String(values.notification_timing || "").trim() !== "") {
      const n = clampTiming(values.notification_timing);
      if (n == null) {
        errs.notification_timing = ["Il promemoria deve essere un numero tra 1 e 31, oppure vuoto per usare il default."];
      }
    }

    // Valida email SOLO se promemoria attivo (e userà default se vuoto)
    if (reminderEnabled) {
      const effective = buildEffectiveNotifications({
        timingInput: values.notification_timing,
        emailInput: values.notification_email,
        defaultDays: defaults.default_days,
        defaultEmailCsv,
      });

      // se non abbiamo email effettive => errore
      if (!effective.effectiveEmail) {
        errs.notification_email = ["Devi inserire almeno un indirizzo email (o avere destinatari di default)."];
      } else {
        // check invalid (solo su quelle effettive)
        const list = splitEmails(effective.effectiveEmail);
        const invalids = list.filter((e) => !isEmail(e));
        if (invalids.length) {
          errs.notification_email = [`Alcuni indirizzi non sono validi: ${invalids.join(", ")}`];
        }
      }
    }

    return errs;
  }, [values, file, reminderEnabled, defaults.default_days, defaultEmailCsv]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors(null);

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    try {
      setSubmitting(true);
      
      // confronto SEMPRE con document-recipients-default:
      // - se “effective” == default => mando null/null
      // - altrimenti mando custom per entrambi

      const effective = buildEffectiveNotifications({
        timingInput: values.notification_timing,
        emailInput: values.notification_email,
        defaultDays: defaults.default_days,
        defaultEmailCsv,
      });

      // se promemoria “spento” (effectiveTiming null) => null/null
      // NB: se defaultDays è null e input vuoto -> effectiveTiming null => promemoria disabilitato
      const timingEnabled = effective.effectiveTiming != null && effective.effectiveTiming >= 1;

      let notification_timing = null;
      let notification_email = null;

      if (values.due_date) {
        const effective = buildEffectiveNotifications({
          timingInput: values.notification_timing,
          emailInput: values.notification_email,
          defaultDays: defaults.default_days,
          defaultEmailCsv,
        });

        const timingEnabled = effective.effectiveTiming != null && effective.effectiveTiming >= 1;

        if (timingEnabled) {
          if (effective.matchesDefault) {
            notification_timing = null;
            notification_email = null;
          } else {
            notification_timing = effective.effectiveTiming;
            notification_email = effective.effectiveEmail || null;
          }
        }
      }

      const payload = {
        ...values,
        visible_to_employee: values.visible_to_employee ? "true" : "false",
        notify_employee: values.notify_employee ? "true" : "false",
        notification_timing,
        notification_email,
        file,
      };

      const res = await uploadDocumentiSingolo(token, payload);

      Swal.fire({
        icon: "success",
        title: "Documento caricato",
        text: res?.message || "Documento caricato con successo.",
        timer: 1500,
        showConfirmButton: false,
      });

      setValues(getInitialValues(employeeId));
      setFile(null);

      onSuccess?.(res);
    } catch (err) {
      if (err?.response?.data && typeof err.response.data === "object") {
        setErrors(err.response.data);
        Swal.fire({
          icon: "error",
          title: "Errore",
          text: err?.response?.data?.error || err?.message || "Si è verificato un errore.",
        });
      } else {
        setErrors({ generico: [err?.message || "Si è verificato un errore."] });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="relative">
        {disabled && <div className="absolute inset-0 rounded-md bg-white/30 cursor-not-allowed z-10" />}

        {errors && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {Object.entries(errors).map(([k, v]) => (
              <div key={k}>
                <strong>{k}:</strong> {Array.isArray(v) ? v.join(", ") : String(v)}
              </div>
            ))}
          </div>
        )}

        <input type="hidden" name="employee_id" value={values.employee_id ?? ""} />

        {/* Nome */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="name">
            Nome Documento *
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={values.name}
            onChange={handleChange}
            className="shadow border rounded w-full py-2 px-3 hover:border-indigo-400 focus:border-indigo-200 focus:ring-2 focus:ring-indigo-200"
          />
          {errors?.name && <p className="text-red-600 text-xs mt-1">{errors.name.join(", ")}</p>}
        </div>

        {/* Descrizione */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="description">
            Descrizione
          </label>
          <input
            id="description"
            name="description"
            type="text"
            value={values.description}
            onChange={handleChange}
            className="shadow border rounded w-full py-2 px-3 hover:border-indigo-400 focus:border-indigo-200 focus:ring-2 focus:ring-indigo-200"
          />
        </div>

        {/* Scadenza */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label
              className="block text-sm font-bold text-gray-700"
              htmlFor="due_date"
            >
              Data Scadenza
            </label>
            {values.due_date && (
              <button
                type="button"
                onClick={() => {
                  setField("due_date", "");
                  setErrors((prev) => {
                    if (!prev) return prev;
                    const next = { ...prev };
                    delete next.due_date;
                    return Object.keys(next).length ? next : null;
                  });
                }}
                className="text-xs text-red-500 underline hover:text-red-600"
              >
                Rimuovi data
              </button>
            )}
          </div>
          <input
            id="due_date"
            name="due_date"
            type="date"
            value={values.due_date}
            min="2000-01-01"
            max="2200-12-31"
            onChange={handleChange}
            inputMode="numeric"
            className={`shadow border border-gray-500 rounded w-full py-2 px-3 focus:ring-2 ${
              errors?.due_date
                ? "border-red-400 bg-red-50 focus:border-red-400 focus:ring-red-100"
                : "border-gray-300 hover:border-indigo-400 focus:border-indigo-200 focus:ring-indigo-200"
            }`}
          />

          {errors?.due_date && (
            <p className="text-red-600 text-xs mt-1">
              {errors.due_date.join(", ")}
            </p>
          )}

          {!!values.due_date && !employeeIsActive && (
            <p className="mt-4 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-3 py-2">
              Il dipendente non è attualmente attivo. Puoi configurare la scadenza e la relativa notifica, che verrà inviata solo se il dipendente sarà attivo al momento dell'invio.
            </p>
          )}
        </div>

        {!!values.due_date && (
          <>
            {/* Promemoria */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700" htmlFor="notification_timing">
                Promemoria scadenza (giorni)
              </label>
              <div className="text-xs text-gray-500 mb-2">
                {defaults.default_days != null ? 'Giorni da 1 a 31, campo vuoto per usare l\'impostazione predefinita' : 'Giorni da 1 a 31'}
                {defaults.loaded && defaults.default_days != null ? (
                  <span className="ml-2 text-gray-400">(Default: {defaults.default_days})</span>
                ) : null}
              </div>

              {/* usa text per permettere cancellazione totale */}
              <input
                id="notification_timing"
                name="notification_timing"
                type="text"
                inputMode="numeric"
                placeholder={defaults.loading ? "Caricamento default…" : (defaultTimingStr ? `${defaultTimingStr} giorni` : "Inserire giorni")}
                value={values.notification_timing ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v !== "" && !/^\d+$/.test(v)) return;
                  setValues((prev) => ({ ...prev, notification_timing: v }));
                  setErrors((prev) => {
                    if (!prev) return prev;
                    const next = { ...prev };
                    delete next.notification_timing;
                    delete next.notification_email;
                    delete next.due_date;
                    return Object.keys(next).length ? next : null;
                  });
                }}
                onBlur={() => {
                  const raw = String(values.notification_timing || "").trim();
                  if (raw === "") return;
                  const n = clampTiming(raw);
                  if (n != null) setValues((prev) => ({ ...prev, notification_timing: String(n) }));
                }}
                className="shadow border rounded w-full py-2 px-3 border-gray-500 hover:border-indigo-400 focus:border-indigo-200 focus:ring-2 focus:ring-indigo-200"
              />
              {errors?.notification_timing && (
                <p className="text-red-600 text-xs mt-1">{errors.notification_timing.join(", ")}</p>
              )}
            </div>

            {/* Email destinatari */}
            {recipientsVisible && (
              <>
                {recipientsDisabled && (
                  <div className="mb-2 text-sm font-semibold text-gray-800">Promemoria disabilitato</div>
                )}

                <div className={`mb-4 ${recipientsDisabled ? "opacity-40 pointer-events-none" : ""}`}>
                  <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="notification_email">
                    Email di notifica scadenza
                  </label>

                  <div className="border border-gray-500 rounded-md">
                    <RecipientsField
                      token={token}
                      value={shownEmail}
                      isEdit={true}
                      disabled={false}
                      openDirection="down"
                      companyEmail={companyEmail}
                      companyName={companyName}
                      handlers={handlers}
                      othersEmails={others}
                      loadSuggested={loadSuggested}
                      onChange={(commaSeparated) => {
                        setValues((v) => ({ ...v, notification_email: commaSeparated }));
                        setErrors((prev) => {
                          if (!prev) return prev;
                          const next = { ...prev };
                          delete next.notification_email;
                          delete next.notification_timing;
                          return Object.keys(next).length ? next : null;
                        });
                      }}
                      className="bg-gray-100 rounded-md py-2"
                      hasShiftsLevel={hasShiftsLevel}
                    />
                  </div>

                  {!recipientsDisabled && errors?.notification_email && (
                    <p className="text-red-600 text-xs mt-1">{errors.notification_email.join(", ")}</p>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* Checkbox notify_employee */}
        <div className="mb-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="notify_employee"
              checked={!!values.notify_employee}
              onChange={handleChange}
              className="form-checkbox rounded text-indigo-600 focus:ring-indigo-600"
            />
            <span className="ml-2">Notifica il dipendente via email</span>
          </label>
          {errors?.notify_employee && (
            <p className="text-red-600 text-xs mt-1">{errors.notify_employee.join(", ")}</p>
          )}
        </div>

        {/* Checkbox visible_to_employee */}
        <div className="mb-4 py-2">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="visible_to_employee"
              checked={!!values.visible_to_employee}
              onChange={handleChange}
              className="form-checkbox rounded text-indigo-600 focus:ring-indigo-600"
            />
            <span className="ml-2">Visibile al dipendente</span>
          </label>
        </div>

        {/* Hidden fissi */}
        <input type="hidden" name="typology" value={values.typology} />

        {/* File */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-2">File *</label>

          <div
            className="flex items-center justify-center w-full"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const f = e.dataTransfer.files?.[0] || null;
              handleSelectedFile(f);
            }}
          >
            <div className="flex flex-col items-center justify-center w-full h-40 border-2 border-indigo-300 border-dashed rounded-lg cursor-pointer bg-indigo-50 hover:bg-indigo-100">
              <p className="text-center text-sm text-indigo-500">
                {file ? (
                  <span className="font-semibold">{file.name}</span>
                ) : (
                  "Clicca per caricare o trascina qui"
                )}
              </p>
              <p className="text-xs text-indigo-500">PDF/JPG/PNG (max 10MB)</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                handleSelectedFile(f);
              }}
            />
          </div>

          {errors?.file && (
            <p className="text-red-600 text-xs mt-1">{errors.file.join(", ")}</p>
          )}
        </div>

        <div className="flex items-center justify-center mt-4">
          <button
            type="submit"
            className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-400 rounded-md text-white text-md font-semibold disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            disabled={submitting}
          >
            {submitting ? "Caricamento..." : "Carica"}
          </button>
        </div>
      </div>
    </form>
  );
}